import type { Checkin } from '@respiria/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { Rating } from '@/components/ui/StatTile';
import { useT } from '@/i18n/useT';
import { api } from '@/lib/api';
import { newClientId } from '@/lib/format';
import { sendOrQueue } from '@/lib/offline-queue';
import { invalidateActivity, queryKeys } from '@/lib/query-keys';

interface TodayResponse {
  localDate: string;
  checkin: Checkin | null;
}

export function useTodayCheckin() {
  return useQuery({
    queryKey: queryKeys.checkinToday(),
    queryFn: () => api<TodayResponse>('/api/checkins/today'),
  });
}

interface Draft {
  mood: number | null;
  energy: number | null;
  stress: number | null;
  sleepHours: string;
  exerciseMinutes: string;
  waterMl: string;
  notes: string;
}

const EMPTY: Draft = {
  mood: null,
  energy: null,
  stress: null,
  sleepHours: '',
  exerciseMinutes: '',
  waterMl: '',
  notes: '',
};

/** Empty string means "not answered", which is different from zero. */
function num(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The daily check-in. One screen, under a minute, nothing required. */
export function CheckinSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data } = useTodayCheckin();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saved, setSaved] = useState(false);

  // Reset the confirmation only when the sheet opens. Doing it in the hydration
  // effect below made the post-save refetch (which changes data.checkin from
  // null to the new row) fire it again, bouncing the user out of "Guardado"
  // back into the form — the reason a save appeared to need two taps.
  useEffect(() => {
    if (open) setSaved(false);
  }, [open]);

  // Reopening on a day that is already filled in shows what was answered. Skip
  // once saved so the refetch can't clobber the draft or the confirmation.
  useEffect(() => {
    if (!open || saved) return;
    const checkin = data?.checkin;
    setDraft(
      checkin
        ? {
            mood: checkin.mood,
            energy: checkin.energy,
            stress: checkin.stress,
            sleepHours: checkin.sleepHours?.toString() ?? '',
            exerciseMinutes: checkin.exerciseMinutes?.toString() ?? '',
            waterMl: checkin.waterMl != null ? (checkin.waterMl / 1000).toString() : '',
            notes: checkin.notes ?? '',
          }
        : EMPTY,
    );
  }, [open, data?.checkin, saved]);

  const save = useMutation({
    mutationFn: () => {
      const waterLitres = num(draft.waterMl);
      // Idempotent by (user, date) on the server, so offline it queues under the
      // check-in's clientId and syncs on reconnect without duplicating.
      const clientId = data?.checkin?.clientId ?? newClientId();
      return sendOrQueue(clientId, `/api/checkins/${data!.localDate}`, 'PUT', {
        clientId,
        mood: draft.mood,
        energy: draft.energy,
        stress: draft.stress,
        sleepHours: num(draft.sleepHours),
        exerciseMinutes: toInt(num(draft.exerciseMinutes)),
        waterMl: waterLitres === null ? null : Math.round(waterLitres * 1000),
        notes: draft.notes.trim() || null,
      });
    },
    onSuccess: () => {
      invalidateActivity(queryClient);
      setSaved(true);
    },
  });

  if (saved) {
    return (
      <Sheet open={open} onClose={onClose} title={t('checkin.savedTitle')}>
        <p className="mb-6 text-[15px] leading-relaxed text-muted">{t('checkin.savedBody')}</p>
        <Button className="w-full" onClick={onClose}>
          {t('checkin.continue')}
        </Button>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onClose={onClose} title={t('checkin.sheetTitle')}>
      <div className="space-y-5">
        <Rating value={draft.mood} onChange={(v) => setDraft({ ...draft, mood: v })} label={t('day.mood')} />
        <Rating
          value={draft.energy}
          onChange={(v) => setDraft({ ...draft, energy: v })}
          label={t('day.energy')}
        />
        <Rating
          value={draft.stress}
          onChange={(v) => setDraft({ ...draft, stress: v })}
          label={t('day.stress')}
        />

        <div className="grid grid-cols-3 gap-3">
          <Field
            label={t('day.sleep')}
            suffix="h"
            value={draft.sleepHours}
            onChange={(v) => setDraft({ ...draft, sleepHours: v })}
          />
          <Field
            label={t('day.exercise')}
            suffix="min"
            value={draft.exerciseMinutes}
            onChange={(v) => setDraft({ ...draft, exerciseMinutes: v })}
          />
          <Field
            label={t('checkin.water')}
            suffix="l"
            value={draft.waterMl}
            onChange={(v) => setDraft({ ...draft, waterMl: v })}
          />
        </div>

        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-muted">{t('checkin.note')}</span>
          <textarea
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            rows={3}
            maxLength={2000}
            className="w-full resize-none rounded-[var(--radius-btn)] bg-surface-2 p-3 text-[15px] text-content"
          />
        </label>

        <Button className="w-full" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending
            ? t('common.saving')
            : data?.checkin
              ? t('checkin.updateBtn')
              : t('checkin.saveBtn')}
        </Button>
        <p className="text-center text-[12px] text-muted">{t('checkin.allOptional')}</p>
      </div>
    </Sheet>
  );
}

function toInt(value: number | null): number | null {
  return value === null ? null : Math.round(value);
}

function Field({
  label,
  suffix,
  value,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] text-muted">
        {label}
        {suffix ? ` (${suffix})` : ''}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="decimal"
        className="tabular min-h-11 w-full rounded-[var(--radius-btn)] bg-surface-2 px-2 text-center text-[15px] text-content"
      />
    </label>
  );
}
